import { Pane, Heading } from 'evergreen-ui';
import { useAuth } from '../services/authContext';
import Calendar from './Calendar';
import AppointmentDialog from './AppointmentDialog';
import ViewEntryDialog from './ViewEntryDialog';
import DescriptionDialog from './DescriptionDialog';
import EditDescriptionDialog from './EditDescriptionDialog';
import EditNoteDialog from './EditNoteDialog';
import EventBox from './EventBox';
import { formatTime } from '../utils/timeUtils';
import useDiary, { SLOT_HEIGHT, DAY_HOURS, DAY_HEIGHT } from './Diary/useDiary';

const Diary = ({ guests = [], diaryEntries: propEntries, setDiaryEntries, isDirector, isEducator }) => {
  const {
    entries,
    futureAppointments,
    selectedDate,
    setSelectedDate,
    userId,
    containerRef,
    isMobile,
    form,
    editingEntry,
    setEditingEntry,
    editingNote,
    setEditingNote,
    showAddDialog,
    setShowAddDialog,
    showViewDialog,
    setShowViewDialog,
    showDescriptionDialog,
    setShowDescriptionDialog,
    showEditDescriptionDialog,
    setShowEditDescriptionDialog,
    showEditNoteDialog,
    setShowEditNoteDialog,
    fetchEntries,
    onEventClick,
    onEmptyDoubleClick,
    handleAddSubmit,
    handleDelete,
  } = useDiary({ guests, diaryEntries: propEntries, setDiaryEntries, isDirector, isEducator });

  return (
    <Pane>
      <Heading size={600} marginBottom={16}>Diario</Heading>

      <Pane display={isMobile ? 'block' : 'flex'} gap={16} marginBottom={16}>
        <div style={{ minWidth: isMobile ? '100%' : 360 }}>
          <Calendar selected={selectedDate} setSelected={setSelectedDate} appointments={futureAppointments} />
        </div>

        <Pane flex={1} display="flex" flexDirection="column" gap={12}>
          <div className="diary-list">
            <div style={{ marginBottom: 12 }}>
              {(() => {
                const descrizioni = (entries || []).filter(en => en.entry_type === 'descrizione');
                if (!descrizioni || descrizioni.length === 0) return null;
                const groups = {};
                descrizioni.forEach(d => {
                  const dateOnly = d.timestamp ? new Date(d.timestamp).toLocaleDateString() : '';
                  const userKey = d.user_id ? String(d.user_id) : `${d.nome || ''}_${d.cognome || ''}`;
                  const key = `${dateOnly}__${userKey}`;
                  if (!groups[key]) groups[key] = { authorName: `${d.nome || ''} ${d.cognome || ''}`.trim(), date: dateOnly, items: [] };
                  groups[key].items.push(d);
                });

                return Object.values(groups).map((g, gi) => (
                  <Pane key={`${g.authorName}_${g.date}_${gi}`} marginBottom={8} padding={12} border="muted" background="#fafafa">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <strong>{g.authorName}</strong> <small>{g.date}</small>
                    </div>
                    <div style={{ marginTop: 8 }}>
                      {g.items.map((it, idx) => (
                        <div key={it.id} style={{ marginTop: idx === 0 ? 8 : 6 }}>
                          <div
                            style={{ cursor: 'pointer', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                            onClick={() => {
                              setEditingEntry(it);
                              if (isDirector) {
                                setShowEditDescriptionDialog(true);
                              } else {
                                setShowViewDialog(true);
                              }
                            }}
                          >{it.testo}</div>
                          {it.attachments && it.attachments.length > 0 && (
                            <div style={{ marginTop: 6 }}>
                              {it.attachments.map(a => (
                                <div key={a.id} style={{ fontSize: 13 }}>
                                  <a href={a.file_url || a.url} target="_blank" rel="noreferrer">{a.file_name || a.name}</a>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </Pane>
                ));
              })()}
            </div>

            <div style={{ display: 'flex', gap: 16 }}>
              <div style={{ width: 90 }}>
                {[...Array(DAY_HOURS).keys()].map(h => (
                  <div key={h} style={{ height: SLOT_HEIGHT, borderBottom: '1px solid #eee', padding: 8, boxSizing: 'border-box', display: 'flex', alignItems: 'center' }}>
                    {String(h).padStart(2, '0')}:00
                  </div>
                ))}
              </div>

              <div style={{ flex: 1, position: 'relative' }}>
                <div ref={containerRef} onDoubleClick={onEmptyDoubleClick} style={{ position: 'relative', height: DAY_HEIGHT, border: '1px solid #f0f0f0', background: '#fff' }}>
                  {(() => {
                    const dayStart = (selDate => { const d = new Date(selDate); d.setHours(0, 0, 0, 0); return d; })(selectedDate || new Date());
                    const events = [];

                    (entries || []).forEach((entry) => {
                      if (entry.entry_type === 'appuntamento' && entry.start_time) {
                        const s = new Date(entry.start_time);
                        const e = entry.end_time ? new Date(entry.end_time) : new Date(s.getTime() + 30 * 60000);
                        events.push({ id: `e_${entry.id}`, start: s, end: e, type: 'entry', data: entry });
                      }

                      if (entry.notes && entry.notes.length) {
                        entry.notes.forEach((n, ni) => {
                          const or = n.start_orario || n.orario || null;
                          if (or) {
                            const parts = or.split(':');
                            const hh = parseInt(parts[0] || '0', 10);
                            const mm = parseInt(parts[1] || '0', 10);
                            const s = new Date(dayStart.getTime());
                            s.setHours(hh, mm, 0, 0);
                            const e = n.end_orario
                              ? (() => { const p = n.end_orario.split(':'); const hh2 = parseInt(p[0] || '0', 10); const mm2 = parseInt(p[1] || '0', 10); const ed = new Date(dayStart.getTime()); ed.setHours(hh2, mm2, 0, 0); return ed; })()
                              : new Date(s.getTime() + 30 * 60000);
                            events.push({ id: `n_${entry.id}_${ni}`, start: s, end: e, type: 'note', data: n, parent: entry });
                          }
                        });
                      }
                    });

                    if (events.length === 0) return <div style={{ padding: 12 }}>Nessuna registrazione per questa data.</div>;

                    events.sort((a, b) => a.start - b.start || a.end - b.end);

                    const assigned = [];
                    const columns = [];
                    events.forEach(ev => {
                      let placed = false;
                      for (let c = 0; c < columns.length; c++) {
                        const last = columns[c][columns[c].length - 1];
                        if (ev.start >= last.end) {
                          columns[c].push(ev);
                          assigned.push({ ev, col: c });
                          placed = true;
                          break;
                        }
                      }
                      if (!placed) {
                        columns.push([ev]);
                        assigned.push({ ev, col: columns.length - 1 });
                      }
                    });

                    const rendered = assigned.map(a => {
                      const ev = a.ev;
                      const overlaps = assigned.filter(x => !(x.ev.end <= ev.start || x.ev.start >= ev.end));
                      const colsInGroup = Math.max(...overlaps.map(x => x.col)) - Math.min(...overlaps.map(x => x.col)) + 1;
                      const colIndex = a.col - Math.min(...overlaps.map(x => x.col));
                      return { ev, colIndex, colsInGroup };
                    });

                    return rendered.map((r) => {
                      const { ev, colIndex, colsInGroup } = r;
                      const startMinutes = (ev.start.getHours() * 60 + ev.start.getMinutes());
                      const endMinutes = (ev.end.getHours() * 60 + ev.end.getMinutes());
                      const top = (startMinutes * (SLOT_HEIGHT / 60));
                      let height = Math.max(20, (endMinutes - startMinutes) * (SLOT_HEIGHT / 60));
                      if (height <= 0) height = 20;

                      const leftPercent = (colIndex / colsInGroup) * 100;
                      const widthPercent = (1 / colsInGroup) * 100;

                      const boxStyle = {
                        position: 'absolute',
                        top: top,
                        left: `${leftPercent}%`,
                        width: `calc(${widthPercent}% - 6px)`,
                        height: height,
                        padding: 6,
                        boxSizing: 'border-box',
                        border: '1px solid #ddd',
                        background: ev.type === 'entry' ? '#e8f4ff' : '#fff6e6',
                        overflow: 'hidden',
                        borderRadius: 4,
                        marginLeft: 6,
                        cursor: 'pointer',
                      };

                      return (
                        <div key={ev.id} style={boxStyle} onClick={(evt) => { evt.stopPropagation(); onEventClick(ev); }}>
                          <EventBox ev={ev} onClick={(evt) => { evt.stopPropagation(); onEventClick(ev); }} formatTime={formatTime} />
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>
          </div>
        </Pane>
      </Pane>

      <AppointmentDialog
        isShown={showAddDialog}
        onClose={() => { setShowAddDialog(false); setEditingEntry(null); }}
        onSave={(payload) => handleAddSubmit(payload)}
        onDelete={(id) => handleDelete(id)}
        entry={editingEntry}
        guests={guests}
        canEdit={isDirector || isEducator}
        userId={userId}
        selectedDate={selectedDate}
      />

      <DescriptionDialog
        isShown={showDescriptionDialog}
        onClose={() => { setShowDescriptionDialog(false); setEditingEntry(null); }}
        onSave={async () => { const fresh = await fetchEntries(selectedDate); if (setDiaryEntries) setDiaryEntries(fresh || []); }}
        userId={userId}
        selectedDate={selectedDate}
      />

      <ViewEntryDialog
        isShown={showViewDialog}
        onClose={() => { setShowViewDialog(false); setEditingEntry(null); }}
        entry={editingEntry}
      />

      <EditDescriptionDialog
        isShown={showEditDescriptionDialog}
        onClose={() => { setShowEditDescriptionDialog(false); setEditingEntry(null); }}
        descriptionEntry={editingEntry}
        currentUserIsDirector={isDirector}
        onSaved={async () => { const fresh = await fetchEntries(selectedDate, true); if (setDiaryEntries) setDiaryEntries(fresh || []); }}
      />

      <EditNoteDialog
        isShown={showEditNoteDialog}
        onClose={() => { setShowEditNoteDialog(false); setEditingNote(null); setEditingEntry(null); }}
        note={editingNote ? (editingNote.data || editingNote) : null}
        guests={guests}
        currentUserIsDirector={isDirector}
        onSaved={async () => { const fresh = await fetchEntries(selectedDate, true); if (setDiaryEntries) setDiaryEntries(fresh || []); }}
      />
    </Pane>
  );
};

export default Diary;
